import { json, LoaderFunctionArgs, MetaFunction } from "@remix-run/node";
import { useLoaderData } from "@remix-run/react";
import { useState, useEffect } from "react";
import { authenticate } from "~/utils/authenticate";
import { getSession } from "~/sessions";
import { BASE_URL } from "~/constants/api";
import { useSavedJobs, SavedJob } from "~/hooks/useSavedJobs";
import { JobCard } from "~/features/jobs/JobCard";
import { Button } from "~/components/Button";
import { Job } from "~/types/Job";
import { Card } from "~/components/Card";
import { Badge } from "~/components/Badge";
import { PageTitle } from "~/components/PageTitle";
import { CollapsibleSection } from "~/components/CollapsibleSection";
import { splitSavedJobs } from "~/utils/savedJobs";

// Signed-in visitors get the compact app header (see root.tsx RouteHandle).
export const handle = { appShell: true, footer: "slim" };
export { headers } from "~/defaults.server";

const FREE_VIEW_LIMIT = 5;
// Inactive bookmarks are revealed this many at a time.
const INACTIVE_PAGE_SIZE = 10;

export const meta: MetaFunction = () => [
  { title: "Saved Jobs — Next Level Jobs EU" },
];

export async function loader({ request }: LoaderFunctionArgs) {
  let isPaidUser = false;
  let savedJobs: SavedJob[] = [];

  try {
    const subscription = await authenticate(request, true);
    isPaidUser = Boolean(subscription?.active);
  } catch {
    // unauthenticated or non-paid — that's fine
  }

  const session = await getSession(request.headers.get("Cookie"));
  const token = session.get("accessToken");
  if (token) {
    try {
      const res = await fetch(`${BASE_URL}/user-saved-jobs`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data: Array<{
          jobId: string;
          jobData?: Job;
          updatedAt: number;
        }> = await res.json();
        const results = await Promise.all(
          data
            .filter((d) => d.jobId)
            .map(async (d): Promise<SavedJob | null> => {
              // Use stored job_data when it is complete
              if (d.jobData?.id && d.jobData?.company) {
                return { job: d.jobData as Job, savedAt: d.updatedAt * 1000 };
              }
              // Fall back to fetching the full job from the API.
              // Note: /jobs/:id without include_location returns the job object
              // directly, not wrapped in { job: ... }.
              try {
                const jobRes = await fetch(`${BASE_URL}/jobs/${d.jobId}`);
                // 410 Gone still carries the job: keep expired bookmarks.
                if (!jobRes.ok && jobRes.status !== 410) return null;
                const job: Job = await jobRes.json();
                return job?.id ? { job, savedAt: d.updatedAt * 1000 } : null;
              } catch {
                return null;
              }
            }),
        );
        savedJobs = results.filter((r): r is SavedJob => r !== null);
      }
    } catch {}
  }

  return json({ isPaidUser, savedJobs });
}

export default function SavedJobs() {
  const { isPaidUser, savedJobs: loaderSavedJobs } =
    useLoaderData<typeof loader>();
  const {
    savedJobs: hookSavedJobs,
    hydrated,
    isLoggedIn,
  } = useSavedJobs();

  // Logged-in: data comes from the server loader (no flash, no mount wait)
  // Anon: data comes from localStorage via the hook (needs hydration)
  const savedJobs = isLoggedIn ? loaderSavedJobs : hookSavedJobs;

  const [mounted, setMounted] = useState(false);
  const [inactiveShown, setInactiveShown] = useState(INACTIVE_PAGE_SIZE);
  useEffect(() => setMounted(true), []);

  if (!isLoggedIn && (!mounted || !hydrated)) {
    return (
      <div>
        <PageTitle title="Saved jobs" />
        <p className="text-gray-400">Loading…</p>
      </div>
    );
  }

  const { active: activeJobs, inactive: offlineJobs } =
    splitSavedJobs(savedJobs);

  const visibleActive = isPaidUser
    ? activeJobs
    : activeJobs.slice(0, FREE_VIEW_LIMIT);
  const hiddenActive = isPaidUser ? [] : activeJobs.slice(FREE_VIEW_LIMIT);
  return (
    <div>
      <PageTitle
        title="Saved jobs"
        trailing={
          savedJobs.length > 0 && (
            <Badge tone="gray" className="text-sm">
              {savedJobs.length}
            </Badge>
          )
        }
        subtitle="Jobs you bookmarked. Tap the bookmark again to remove one."
      />

      {savedJobs.length === 0 ? (
        <div className="rounded-2xl border-2 border-dashed border-gray-800 py-16 text-center">
          <p className="mb-4 text-gray-400">
            Nothing saved yet. Use the bookmark on any job to keep it here.
          </p>
          <Button variant="outlined" to={isPaidUser ? "/jobs" : "/"}>
            Browse jobs
          </Button>
        </div>
      ) : (
        <div className="flex flex-col gap-6">
          {/* Active jobs */}
          {visibleActive.length > 0 || hiddenActive.length > 0 ? (
            <div className="flex flex-col gap-0">
              <ul
                role="list"
                className="overflow-hidden bg-gray-700 shadow-sm ring-1 ring-gray-900/5 rounded-xl"
              >
                {visibleActive.map(({ job }) => (
                  <JobCard
                    key={job.id}
                    job={job}
                    isPaidUser={isPaidUser}
                    showSaveButton
                  />
                ))}
              </ul>
            </div>
          ) : null}

          {/* Bookmarks whose job is gone: folded away, revealed in pages */}
          {offlineJobs.length > 0 && (
            <CollapsibleSection
              title="No longer online"
              summary={`${offlineJobs.length} ${offlineJobs.length === 1 ? "bookmark" : "bookmarks"}`}
            >
              <ul
                role="list"
                className="overflow-hidden bg-gray-700 shadow-sm ring-1 ring-gray-900/5 rounded-xl"
              >
                {offlineJobs.slice(0, inactiveShown).map(({ job }) => (
                  <JobCard
                    key={job.id}
                    job={job}
                    isPaidUser={isPaidUser}
                    showSaveButton
                  />
                ))}
              </ul>
              {offlineJobs.length > inactiveShown && (
                <div className="mt-4 flex justify-center">
                  <Button
                    size="sm"
                    variant="subtle"
                    className="py-2"
                    onClick={() =>
                      setInactiveShown((shown) => shown + INACTIVE_PAGE_SIZE)
                    }
                  >
                    Show {Math.min(INACTIVE_PAGE_SIZE, offlineJobs.length - inactiveShown)} more
                    <span className="text-gray-500">
                      {" "}
                      ({offlineJobs.length - inactiveShown} left)
                    </span>
                  </Button>
                </div>
              )}
            </CollapsibleSection>
          )}

          {/* Upgrade CTA for non-paid users */}
          {!isPaidUser && (
            <Card
              className="max-w-3xl justify-stretch w-full flex flex-col gap-8 bg-gradient-to-r from-lime-400 via-purple-400 to-gray-400 bg-[length:400%_400%] animate-border border-none"
              style={{ padding: "2px" }}
            >
              <div className="block bg-gray-900 rounded-2xl overflow-hidden p-0.5 sm:p-0.5 p-4">
                <div className="rounded-xl border border-gray-700/50 bg-gray-800/50 px-6 py-8 text-center">
                  <p className="text-white font-semibold mb-1">
                    Upgrade to Pro
                  </p>
                  <p className="text-sm text-gray-400 mb-5">
                    {isLoggedIn
                      ? "Get unlimited saves, personalised job alerts, and more."
                      : "Save unlimited jobs, get personalised alerts, and more."}
                  </p>
                  <Button variant="primary" to="/signup">
                    Get full access
                  </Button>
                </div>
              </div>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
